import { useRef, useState } from 'react';
import { keepPreviousData, useQueries, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api';
import { leadEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import type { Lead, LeadListParams, LeadListResponse } from '@/types/lead';

export const BOARD_COLUMN_PAGE_SIZE = 50;

// Cached pages stay fresh across "Load more" clicks so prior pages are served
// from cache instead of re-fetched. A stage move / filter change invalidates
// them (via lists()) regardless of this window, so the board still refreshes.
const BOARD_COLUMN_PAGE_STALE_MS = 60_000;

export interface LeadBoardColumn {
  items: Lead[];
  hasMore: boolean;
  /** First page still loading (no data yet) — show a column skeleton. */
  isLoading: boolean;
  /** The column query failed — show an error + retry affordance. */
  isError: boolean;
  /** A "Load more" request is specifically in flight (not a background refetch). */
  isFetchingNextPage: boolean;
  loadMore: () => void;
  refetch: () => void;
}

export interface LeadBoardColumns {
  columns: Record<string, LeadBoardColumn>;
  isLoading: boolean;
}

function fetchColumnPage(
  baseParams: LeadListParams,
  stageId: string,
  page: number,
): Promise<LeadListResponse> {
  return apiGet<LeadListResponse>(operationsClient, leadEndpoints.list(), {
    params: {
      ...baseParams,
      stage: stageId,
      page,
      limit: BOARD_COLUMN_PAGE_SIZE,
    } as Record<string, unknown>,
  });
}

/**
 * Pull pages 1..pageCount for one stage through `fetchQuery`, so already fetched
 * pages that are still fresh are cache hits and only the newest page actually
 * goes to the network — "Load more" is O(1) requests, not O(pageCount). Crucial:
 * `fetchQuery` refetches when a page is stale OR invalidated, so a stage move
 * (which invalidates `lists()`) forces fresh pages instead of serving the stale
 * cache — unlike `ensureQueryData`, which returns cached data whenever present.
 * Dedupe by lead id because concurrent offset pages can overlap by one row if a
 * lead is inserted or moved into the stage mid-fetch (a duplicate id would
 * collide as a React / dnd key).
 */
async function fetchColumnPages(
  queryClient: QueryClient,
  baseParams: LeadListParams,
  stageId: string,
  pageCount: number,
): Promise<{ items: Lead[]; hasMore: boolean }> {
  const pages = await Promise.all(
    Array.from({ length: pageCount }, (_, index) =>
      queryClient.fetchQuery({
        queryKey: leadKeys.boardColumnPage(stageId, baseParams, index + 1),
        queryFn: () => fetchColumnPage(baseParams, stageId, index + 1),
        staleTime: BOARD_COLUMN_PAGE_STALE_MS,
      }),
    ),
  );

  const seen = new Set<string>();
  const items: Lead[] = [];
  for (const page of pages) {
    for (const lead of page.data) {
      if (!seen.has(lead.id)) {
        seen.add(lead.id);
        items.push(lead);
      }
    }
  }
  return {
    items,
    hasMore: pages[pages.length - 1]?.hasMore ?? false,
  };
}

/**
 * NOT-514 #2: per-column pagination for the leads board. Runs one aggregate
 * query per stage (product wants every card reachable on the board, not a hard
 * 100-lead slice); each column loads more independently. Truthful per-column
 * counts/$ come from the pipeline summary — this hook owns only the cards.
 *
 * `stageIds` order must be stable across renders (it comes from the loaded
 * stage list); `useQueries` runs the whole set in one hook call, so a changing
 * length is safe.
 */
export function useLeadBoardColumns(
  stageIds: string[],
  baseParams: LeadListParams,
): LeadBoardColumns {
  const queryClient = useQueryClient();
  const [pageCounts, setPageCounts] = useState<Record<string, number>>({});

  // Reset every column to page 1 when the filter set changes. Done during
  // render (React's supported "reset state on prop change" pattern) so the
  // restarted render runs at depth 1 — avoids eagerly refetching the previous
  // depth against the new filters.
  const paramsKey = JSON.stringify(baseParams);
  const prevParamsKey = useRef(paramsKey);
  if (prevParamsKey.current !== paramsKey) {
    prevParamsKey.current = paramsKey;
    setPageCounts({});
  }

  const results = useQueries({
    queries: stageIds.map((stageId) => {
      const pageCount = pageCounts[stageId] ?? 1;
      return {
        queryKey: leadKeys.boardColumn(stageId, baseParams, pageCount),
        queryFn: () => fetchColumnPages(queryClient, baseParams, stageId, pageCount),
        placeholderData: keepPreviousData,
        // Failed columns render their own inline error+retry; suppress the
        // global toast so N failing stages don't fire N toasts.
        meta: { suppressToast: true },
      };
    }),
  });

  const columns: Record<string, LeadBoardColumn> = {};
  stageIds.forEach((stageId, index) => {
    const result = results[index];
    columns[stageId] = {
      items: result?.data?.items ?? [],
      hasMore: result?.data?.hasMore ?? false,
      isLoading: result?.isLoading ?? true,
      isError: result?.isError ?? false,
      // A load-more shows the previous depth as placeholder while the larger
      // page-set resolves (isPlaceholderData); a background refetch of the same
      // key does not — so this stays false on stage-move / focus refetches.
      isFetchingNextPage: Boolean(result?.isFetching && result?.isPlaceholderData),
      loadMore: () =>
        setPageCounts((prev) => ({
          ...prev,
          [stageId]: (prev[stageId] ?? 1) + 1,
        })),
      refetch: () => {
        void result?.refetch();
      },
    };
  });

  return {
    columns,
    isLoading: results.some((result) => result.isLoading),
  };
}
